import React from 'react'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { Container } from '../styles/Container'
import Autosuggest from 'react-autosuggest'
import AutosuggestHighlightMatch from 'autosuggest-highlight/match'
import AutosuggestHighlightParse from 'autosuggest-highlight/parse'
import Grid, { Cell } from '../styles/Grid'
import { breakpoint } from './styles'
import { fetchSearchSuggestions } from '../helpers/search'

const getSuggestionValue = (suggestion: any) => suggestion.title;

function renderSuggestion(suggestion: any, { query }: any) {
  const matches = AutosuggestHighlightMatch(suggestion.title, query);
  const parts = AutosuggestHighlightParse(suggestion.title, matches);

  return (
    <span>
      {parts.map((part: any, index: number) => {
        const className = part.highlight ? 'react-autosuggest__suggestion-match' : '';

        return (
          <span className={className} key={index}>
            {part.text}
          </span>
        );
      })}
    </span>
  );
}

const Search = ({ setSearchOpen, translations, contained, path }: any) => {

  const router = useRouter()
  const [keyword, setKeyword] = React.useState<string | string[] | undefined>(contained && router.query.keyword)
  const [suggestions, setSuggestions] = React.useState<any[]>([])
  const abortControllerRef = React.useRef<AbortController | null>(null)
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const onChange = (event: any, { newValue }: any) => {
    setKeyword(newValue)
    if (contained && !newValue) {
      router.push(`${path}?keyword=`)
    }
  }

  const onKeyUp = (event: any) => {
    if (event.keyCode === 13) {
      redirectToSearchResults()
    }
  }

  const onSuggestionsFetchRequested = ({ value }: any) => {
    if (translations?.search?.turn_suggestions_off) {
      setSuggestions([])
      return
    }

    // Cancel any pending debounce timer
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

    debounceTimerRef.current = setTimeout(async () => {
      // Abort any in-flight request
      if (abortControllerRef.current) abortControllerRef.current.abort()
      abortControllerRef.current = new AbortController()

      try {
        const results = await fetchSearchSuggestions(value, 6, abortControllerRef.current.signal)
        setSuggestions(results)
      } catch (e: any) {
        // Ignore aborted requests; they are superseded by a newer keystroke.
        // Non-abort errors are swallowed here because re-throwing inside a
        // detached async setTimeout callback produces an unhandled promise
        // rejection — there is no caller to catch it.
        if (e?.name !== 'AbortError') {
          console.error('Search suggestions fetch failed:', e)
        }
      }
    }, 300)
  }

  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const clearSearchBox = () => {
    setKeyword("")
  }

  const redirectToSearchResults = () => {
    // Route keyword searches to the dedicated SSR /search page to avoid
    // polluting ISR caches on /products and /recipes with per-keyword entries.
    const type = path.replace(/^\//, '') || 'products'
    router.push(`/search?type=${type}&keyword=${encodeURIComponent((keyword as string) || '')}`)
    setSearchOpen(false)
  }

  const redirectToResult = (e: any, { suggestion }: any) => {
    const category = suggestion?.filters?.[0]?.value || 'uncategorized';
    router.push(`/${'product_details' in suggestion ? 'products' : 'recipes'}/${category}/${suggestion.url_token}`)
    setSearchOpen(false)
  }

  const inputProps = {
    placeholder: translations.search.placeholder,
    autoFocus: true,
    onChange,
    onKeyUp
  }

  React.useEffect(() => {
    setKeyword(router.query.keyword?.toString() || '')
  }, [router.query.keyword])

  // Clean up debounce timer and any in-flight request when the component unmounts
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  return (
    <SearchContainer {...{ contained }}>
      <Container style={{ margin: '0 auto' }}>
        <Grid fixed>
          <Cell xs={8} md={12} style={{ position: 'relative' }}>
            <Autosuggest
              suggestions={suggestions}
              onSuggestionsFetchRequested={onSuggestionsFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              onSuggestionSelected={redirectToResult}
              getSuggestionValue={getSuggestionValue}
              shouldRenderSuggestions={(value: string) => value?.length > 1 && !contained}
              renderSuggestion={renderSuggestion}
              inputProps={{
                ...inputProps,
                value: keyword?.toString() || '',
              }}
            />
            <ClearButton onClick={clearSearchBox}>
              <img src="/images/clear.svg" alt="close" role="button" onClick={(e) => onChange(e, "")} width={25} height={18} />
            </ClearButton>
            <SearchButton onClick={redirectToSearchResults}>
              <img src="/images/search-light.svg" alt="search" role="button" width={21} height={20} />
            </SearchButton>
          </Cell>
          <Cell xs={2} md={'0'}>
            <CloseButton src="/images/close.svg" alt="close" role="button" width={31} height={30}
              onClick={() => !contained ? setSearchOpen(false) : router.push(router.asPath.split('?')[0])}
              {...{ contained }} />
          </Cell>
        </Grid>
      </Container>
    </SearchContainer>)
}

export default Search

export const SearchWrapper = styled(Container)`
  height: 200px;
  position: relative;
  padding: 0 1.5rem;
  color: var(--text);
  @media ${breakpoint.desktop} {
    padding: 0;
  }
`

const CloseButton = styled.img<any>`
  margin-left: 12px;
  margin-top: 12px;
  @media ${breakpoint.desktop} {
    margin-left: 36px;
    margin-top: 21px;
  }
  cursor: pointer;
  ${props => props.contained && css`
    filter: brightness(0.9);
  `}
`

export const SearchButton = styled.button`
  position: absolute;
  right: 1px;
  top: 1px;
  width: 47px;
  height: 47px;
  @media ${breakpoint.desktop} {
    top: 2px;
    width: 70px;
    height: 70px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  border: 0;
  cursor: pointer;
`

export const ClearButton = styled(SearchButton)`
  right: 50px;
  background: rgba(0,0,0,0);
  @media ${breakpoint.desktop} {
    right: 72px;
  }
`

export const SearchContainer = styled.div<any>`
  position: absolute;
  margin-top: ${props => props.contained ? '10px' : '48px'};
  width: 100%;
  .react-autosuggest__input {
    padding: 12px;
    @media ${breakpoint.desktop} {
      padding: 24px;
    }
    width: 100%;
    border: 2px #000 solid;
    margin: 0;
    outline: 0;
    font-size: 16px;
    font-weight: 700;
    &::placeholder {
      font-weight: normal;
    }
  }
  .react-autosuggest__suggestions-container {
    background: #fff;
    ul {
      margin: 0;
      padding: 12px 0;
      list-style: none;
      li {
        margin: 0;
        padding: 12px;
        @media ${breakpoint.desktop} {
          padding: 12px 24px;
        }
      }
    }
  }
  .react-autosuggest__suggestion--highlighted {
    background: rgba(0,0,0,0.1);
  }
  .react-autosuggest__suggestion-match {
    font-weight: bold;
  }
`
